/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useState } from 'react';
import { DateRange } from '@material-ui/pickers';
import { Grid, IconButton, MenuItem } from '@material-ui/core';
import { SearchOutlined } from '@material-ui/icons';
import moment from 'moment/moment';
import { useApiError, useAppData } from '../../../providers';
import { useStyles } from './styles';
import { useCommonStyles } from '../../../styles';
import { DateRangeSelect } from '../../DateRangeSelect';
import { CheckIcon, HamburgerIcon } from '../../CustomIcons';
import { MultiSelectControl } from '../../Controls';
import { getPartnerPartnerships } from '../../../pages';
import { PartnerPartnership } from '../../../models';
import { getPartnersLogins } from './ReportGeneralToolbarWithFiltersService';
import { SpinnerWithProps } from '../../SpinnerWithProps';
import { FiltersDialog } from './Filters';

interface Props {
  translation: any;
  dateRange: DateRange<Date>;
  handleDateRangeChange: (date: DateRange<Date>) => void;
  handleButtonClick?: () => void;
  handlePartnershipsChange: (newOptions: string[]) => void;
  handleLoginsChange: (newOptions: string[]) => void;
  applyFilters: (partnerships: string[], logins: string[]) => void;
}

export const ReportGeneralToolbarWithFilters: FC<Props> = ({ translation, dateRange, handleDateRangeChange, handleButtonClick, handleLoginsChange, handlePartnershipsChange, applyFilters }) => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { userData } = useAppData();
  const { addError } = useApiError();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedDateRange, setSelectedDateRange] = React.useState<DateRange<Date>>(dateRange);
  const [partnerships, setPartnerships] = useState<PartnerPartnership[]>([]);
  const [selectedPartnerships, setSelectedPartnerships] = useState<string[]>(['All']);
  const [logins, setLogins] = useState<any[]>([]);
  const [selectedLogins, setSelectedLogins] = useState<string[]>(['All']);
  const [showButton, setShowButton] = useState<boolean>(true);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);

  const getLogins = useCallback(
    async (partnershipCodes: string[]) => {
      const userLoginData = JSON.parse(userData);
      try {
        setLoading(true);
        const response = await getPartnersLogins(moment(selectedDateRange[0]).format('YYYY-MM-DD'), moment(selectedDateRange[1]).format('YYYY-MM-DD'), partnershipCodes, userLoginData.partnerId);
        setLogins(response.data);
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setLoading(false);
      }
    },
    [selectedDateRange, addError, userData],
  );

  const getPartnerships = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPartnerPartnerships(true);
      setPartnerships(response.data);
      await getLogins([]);
    } catch (error) {
      addError(JSON.stringify(error.response));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await getPartnerships();
    })();
  }, []);

  const requestReport = useCallback(() => {
    if (handleButtonClick) {
      handleButtonClick();
      setShowButton(false);
    }
  }, [handleButtonClick]);

  const renderValueCustomCondition = useCallback(
    (value: string[]) => {
      if (value[0] === 'All') {
        return [translation.all];
      }

      return false;
    },
    [translation.all],
  );

  const handlePartnershipSelectChange = useCallback(
    async (event) => {
      let options = event.target.value as string[];
      if (options.length === 0) {
        options = ['All'];
      }

      if (options.length > 1) {
        if (options.includes('All')) {
          if (options[options.length - 1] === 'All') {
            options = options.filter((option) => option === 'All');
          } else {
            options = options.filter((option) => option !== 'All');
          }
        }
      }

      setSelectedPartnerships(options);
      await getLogins(options.includes('All') ? [] : options);
      handlePartnershipsChange(options);
    },
    [handlePartnershipsChange, getLogins],
  );

  const handleLoginSelectChange = useCallback(
    async (event) => {
      let options = event.target.value as string[];
      if (options.length === 0) {
        options = ['All'];
      }

      if (options.length > 1) {
        if (options.includes('All')) {
          if (options[options.length - 1] === 'All') {
            options = options.filter((option) => option === 'All');
          } else {
            options = options.filter((option) => option !== 'All');
          }
        }
      }

      setSelectedLogins(options);
      handleLoginsChange(options);
    },
    [handleLoginsChange],
  );

  return (
    <>
      <Grid container direction="row" justify="space-between" alignContent="center" alignItems="center" className={classes.desktopContainer} spacing={1}>
        <SpinnerWithProps isLoading={loading} />
        <Grid item xs={showButton ? 6 : 4}>
          <p className={commonClasses.formControlTitle}>{translation.text_6166}</p>
          <DateRangeSelect
            defaultValue={selectedDateRange}
            onRangeChange={(value) => {
              setSelectedDateRange(value);
              handleDateRangeChange(value);
              setShowButton(true);
            }}
          />
        </Grid>
        {showButton && (
          <Grid item style={{ alignSelf: 'flex-end' }}>
            <IconButton onClick={requestReport}>
              <SearchOutlined style={{ color: '#030C21' }} />
            </IconButton>
          </Grid>
        )}
        {!showButton && (
          <>
            <Grid item xs={4}>
              <p className={commonClasses.formControlTitle}>{translation.text_6447} </p>
              <MultiSelectControl renderValueCustomCondition={renderValueCustomCondition} value={selectedPartnerships} onChange={(event: any) => handlePartnershipSelectChange(event)}>
                <MenuItem className={selectedPartnerships.includes('All') ? classes.selectedOption : classes.option} value="All">
                  <CheckIcon />
                  <span>{translation.all}</span>
                </MenuItem>
                {partnerships.map((partnership) => (
                  <MenuItem
                    className={selectedPartnerships.includes(partnership.partnershipCode as string) ? classes.selectedOption : classes.option}
                    key={partnership.partnershipCode}
                    value={partnership.partnershipCode ? partnership.partnershipCode : ''}
                  >
                    <CheckIcon />
                    <span>{partnership.partnershipCode}</span>
                  </MenuItem>
                ))}
              </MultiSelectControl>
            </Grid>
            {selectedPartnerships.length > 0 && (
              <Grid item xs={4}>
                <p className={commonClasses.formControlTitle}>{translation.text_6001} </p>
                <MultiSelectControl renderValueCustomCondition={renderValueCustomCondition} value={selectedLogins} onChange={(event: any) => handleLoginSelectChange(event)}>
                  <MenuItem className={selectedLogins.includes('All') ? classes.selectedOption : classes.option} value="All">
                    <CheckIcon />
                    <span>{translation.all}</span>
                  </MenuItem>
                  {logins.map((login) => (
                    <MenuItem className={selectedLogins.includes(login.key) ? classes.selectedOption : classes.option} key={login.key} value={login.key}>
                      <CheckIcon />
                      <span>{login.key}</span>
                    </MenuItem>
                  ))}
                </MultiSelectControl>
              </Grid>
            )}
          </>
        )}
      </Grid>
      <Grid container direction="row" justify="space-between" className={classes.mobileContainer} alignItems="center">
        <Grid item xs={11}>
          <p className={commonClasses.formControlTitle}>{translation.text_6166}</p>
          <DateRangeSelect
            defaultValue={selectedDateRange}
            onRangeChange={(value) => {
              setSelectedDateRange(value);
              handleDateRangeChange(value);
              setShowButton(true);
            }}
          />
        </Grid>
        {showButton && (
          <Grid item xs={1}>
            <IconButton onClick={requestReport}>
              <SearchOutlined style={{ color: '#030C21' }} />
            </IconButton>
          </Grid>
        )}
        {!showButton && (
          <Grid item xs={1}>
            <IconButton onClick={() => setOpenFilterDialog(true)}>
              <HamburgerIcon />
            </IconButton>
          </Grid>
        )}
      </Grid>

      <FiltersDialog
        open={openFilterDialog}
        translation={translation}
        dateRange={selectedDateRange}
        partnerships={partnerships}
        onClose={() => setOpenFilterDialog(false)}
        applyFilter={(partnerships, logins) => {
          setSelectedPartnerships(partnerships);
          setSelectedLogins(logins);
          setOpenFilterDialog(false);
          applyFilters(partnerships, logins);
        }}
        renderValueCustomCondition={renderValueCustomCondition}
      />
    </>
  );
};
